// Gemeinsame WS-/Lauf-Anbindung für Monitor und Anlagenüberwachungs-Dashboard.
//
// Kapselt: Lauf starten (POST /runs) → Monitor abonnieren (WS /monitor) → tick-Werte je Tag
// halten → Lauf stoppen (DELETE) → sauberes Trennen. Zusätzlich zieht sie den Safety-Zustand
// (Watchdog + Ereignisse) aus den status-Nachrichten. Beide Ansichten teilen sich diesen Haken,
// damit die Verbindungs-/Lauf-Logik nicht doppelt gepflegt werden muss.

import { useCallback, useEffect, useRef, useState } from 'react';
import { useI18n } from '../i18n';
import { useStore } from './store';
import {
  approveDeploy,
  connectMonitor,
  listRuns,
  proposeDeploy,
  startRun,
  stopRun,
  updatePace,
  type MonitorConnection,
} from '../api/client';
import type {
  MonitorMessage,
  RunCreate,
  RunSourceSpec,
  SafetyState,
  TickValue,
} from '../api/types';

// Aktuelle Ablaufgeschwindigkeit eines Laufs: Zykluszeit (Regel-dt), Zeitraffer-Faktor und ob der
// Zeitraffer gesperrt ist (Echtzeit 1×, bei Live-OPC). `null`, solange kein Lauf verbunden ist.
export interface PaceState {
  cycleMs: number;
  speed: number;
  lockSpeed: boolean;
}

// Verbindungszustand für die Statusanzeige (idle → connecting → live → lost).
export type ConnState = 'idle' | 'connecting' | 'live' | 'lost';

const MAX_POINTS = 240; // gleitendes Fenster je Reihe (hält die SVG-Pfade schlank)

export interface MonitorRun {
  runId: string | null;
  conn: ConnState;
  busy: boolean;
  // Selbst gestartet (dry-run über „Lauf starten") vs. nur lesend an einen bereits laufenden
  // (z. B. per API/Deploy gestarteten) Lauf angehängt. Nur ein selbst gestarteter Lauf darf
  // gestoppt werden — der Mitleser eines fremden Live-Laufs greift nicht ein.
  owned: boolean;
  // gleitende Trendreihe je Tag (für Charts)
  series: Map<string, { t: number; value: number }[]>;
  // jüngster Tick-Wert je Tag (Wert, Einheit, Qualität, ggf. Modus/Klemmung/Rate)
  latest: Map<string, TickValue>;
  // jüngster Safety-Zustand aus den status-Nachrichten (Watchdog + Ereignisse); null bis dahin
  safety: SafetyState | null;
  // aktuelle Ablaufgeschwindigkeit (Zykluszeit/Zeitraffer/Sperre); null solange nichts verbunden ist
  pace: PaceState | null;
  // Start mit festen Eingangs-Sollwerten (Sim) ODER mit einem vollständigen Lauf-Payload
  // (z. B. CSV-Replay über `csv`). Ein Payload hat Vorrang vor den Sim-Quellen.
  start: (sourcesOrPayload?: RunSourceSpec[] | RunCreate) => Promise<void>;
  // Live-Lauf (OPC-Demonstrator) per Klick: schlägt einen Deploy vor, gibt ihn frei (bei
  // ausgeschaltetem Vier-Augen darf dieselbe Person beides) und startet den Lauf im Modus 'live'
  // gegen den mitgegebenen OPC-Endpoint. Fehlt der Endpoint, wird gar nicht erst gestartet und der
  // Aufrufer über den zurückgegebenen `false`-Wert informiert (die View zeigt dann den Hinweis).
  startLive: (opcEndpoint: string | null | undefined) => Promise<boolean>;
  stop: () => Promise<void>;
  reconnect: () => void;
  // Ablaufgeschwindigkeit live verstellen (Zykluszeit und/oder Zeitraffer). Wirkt sofort; der
  // bestätigte Stand kommt über die WS-`pace`-Nachricht zurück.
  setPace: (pace: { cycleMs?: number; speed?: number }) => Promise<void>;
}

// Ein Lauf-/Monitor-Haken je Ansicht. Beim Verlassen/Projektwechsel trennt er sauber.
export function useMonitorRun(): MonitorRun {
  const { t } = useI18n();
  const { activeProjectId, reportError, notify } = useStore();

  const [runId, setRunId] = useState<string | null>(null);
  const [owned, setOwned] = useState(false);
  const [conn, setConn] = useState<ConnState>('idle');
  const [busy, setBusy] = useState(false);
  const [series, setSeries] = useState<Map<string, { t: number; value: number }[]>>(new Map());
  const [latest, setLatest] = useState<Map<string, TickValue>>(new Map());
  const [safety, setSafety] = useState<SafetyState | null>(null);
  const [pace, setPaceState] = useState<PaceState | null>(null);
  const connectionRef = useRef<MonitorConnection | null>(null);
  // Entprellung der Pace-Netzwerkaufrufe (Slider feuert viele Events; nur der letzte Stand zählt).
  const paceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingPaceRef = useRef<{ cycleMs?: number; speed?: number }>({});

  // Eine WS-Nachricht in Reihen/Werteliste bzw. Safety-Zustand einpflegen.
  const applyMessage = useCallback((msg: MonitorMessage) => {
    if (msg.op === 'error') {
      setConn('lost');
      return;
    }
    if (msg.op === 'status') {
      if (msg.safety) setSafety(msg.safety);
      // Reglerstellung übernehmen, falls der Status sie trägt (frisch verbundener Monitor).
      if (typeof msg.cycleMs === 'number' && typeof msg.speed === 'number') {
        setPaceState({
          cycleMs: msg.cycleMs,
          speed: msg.speed,
          lockSpeed: Boolean(msg.lockSpeed),
        });
      }
      if (msg.engineState === 'stopped') setConn('idle');
      return;
    }
    if (msg.op === 'pace') {
      setPaceState({ cycleMs: msg.cycleMs, speed: msg.speed, lockSpeed: msg.lockSpeed });
      return;
    }
    // tick
    setSeries((prev) => {
      const next = new Map(prev);
      for (const v of msg.values) {
        const arr = next.get(v.tag)?.slice() ?? [];
        arr.push({ t: msg.t, value: v.value });
        if (arr.length > MAX_POINTS) arr.splice(0, arr.length - MAX_POINTS);
        next.set(v.tag, arr);
      }
      return next;
    });
    setLatest((prev) => {
      const next = new Map(prev);
      for (const v of msg.values) next.set(v.tag, v);
      return next;
    });
  }, []);

  const disconnect = useCallback(() => {
    connectionRef.current?.close();
    connectionRef.current = null;
  }, []);

  const openMonitor = useCallback(
    (projectId: string, rid: string) => {
      setConn('connecting');
      connectionRef.current = connectMonitor(projectId, rid, {
        onOpen: () => setConn('live'),
        onMessage: applyMessage,
        onClose: () => {
          setConn((s) => (s === 'live' || s === 'connecting' ? 'lost' : s));
        },
        onError: () => setConn('lost'),
      });
    },
    [applyMessage],
  );

  const start = useCallback(
    async (sourcesOrPayload?: RunSourceSpec[] | RunCreate) => {
      if (!activeProjectId) return;
      setBusy(true);
      setSeries(new Map());
      setLatest(new Map());
      setSafety(null);
      setPaceState(null);
      try {
        // Ein Array sind feste Sim-Eingangs-Sollwerte; ein Objekt ist ein vollständiger Lauf-Payload
        // (z. B. CSV-Replay). So bleibt der bestehende Sim-Aufruf kompatibel.
        let payload: RunCreate | undefined;
        if (Array.isArray(sourcesOrPayload)) {
          payload = sourcesOrPayload.length > 0 ? { sources: sourcesOrPayload } : undefined;
        } else {
          payload = sourcesOrPayload;
        }
        const info = await startRun(activeProjectId, payload);
        setRunId(info.runId);
        setOwned(true);
        setPaceState({
          cycleMs: info.cycleMs,
          speed: info.speed ?? 1,
          lockSpeed: info.lockSpeed ?? false,
        });
        openMonitor(activeProjectId, info.runId);
        notify('success', t('monitor.started', { run: info.runId }));
      } catch (err) {
        reportError(err, t('monitor.startFailed'));
        setConn('idle');
      } finally {
        setBusy(false);
      }
    },
    [activeProjectId, openMonitor, notify, reportError, t],
  );

  const startLive = useCallback(
    async (opcEndpoint: string | null | undefined): Promise<boolean> => {
      if (!activeProjectId) return false;
      // Ohne projektspezifischen OPC-Endpoint fährt der Live-Lauf ins Leere — gar nicht erst
      // starten, sondern die View den Hinweis „Endpoint in den Einstellungen setzen" zeigen lassen.
      if (!opcEndpoint || opcEndpoint.trim() === '') {
        notify('error', t('monitor.live.noEndpoint'));
        return false;
      }
      setBusy(true);
      setSeries(new Map());
      setLatest(new Map());
      setSafety(null);
      setPaceState(null);
      try {
        // Deploy vorschlagen → freigeben (bei ausgeschaltetem Vier-Augen darf dieselbe Person beides)
        // → Live-Lauf gegen den Projekt-OPC-Endpoint starten. So ist ein Live-Lauf per Klick möglich,
        // ohne die harten Backend-Gates (freigegebener Deploy, SAFE-40/41) zu umgehen.
        const proposed = await proposeDeploy(activeProjectId);
        const approved = await approveDeploy(proposed.deployId);
        const info = await startRun(activeProjectId, {
          mode: 'live',
          deployId: approved.deployId,
          opc_url: opcEndpoint,
        });
        setRunId(info.runId);
        setOwned(true);
        setPaceState({
          cycleMs: info.cycleMs,
          speed: info.speed ?? 1,
          lockSpeed: info.lockSpeed ?? false,
        });
        openMonitor(activeProjectId, info.runId);
        notify('success', t('monitor.live.started', { run: info.runId }));
        return true;
      } catch (err) {
        reportError(err, t('monitor.live.failed'));
        setConn('idle');
        return false;
      } finally {
        setBusy(false);
      }
    },
    [activeProjectId, openMonitor, notify, reportError, t],
  );

  const stop = useCallback(async () => {
    // Nur selbst gestartete Läufe darf die HMI stoppen — ein lesend angehängter Fremdlauf nicht.
    if (!activeProjectId || !runId || !owned) return;
    setBusy(true);
    try {
      await stopRun(activeProjectId, runId);
      notify('info', t('monitor.stopped'));
    } catch (err) {
      reportError(err, t('monitor.stopFailed'));
    } finally {
      disconnect();
      setRunId(null);
      setOwned(false);
      setConn('idle');
      setPaceState(null);
      setBusy(false);
    }
  }, [activeProjectId, runId, owned, disconnect, notify, reportError, t]);

  const setPace = useCallback(
    async (change: { cycleMs?: number; speed?: number }) => {
      if (!activeProjectId || !runId) return;
      // Optimistisch sofort spiegeln, damit der Regler beim Ziehen nicht zappelt; die WS-`pace`-
      // Nachricht bestätigt/korrigiert (serverseitige Klemmung, Live-Sperre) kurz darauf.
      setPaceState((prev) =>
        prev
          ? {
              cycleMs: change.cycleMs ?? prev.cycleMs,
              speed: change.speed ?? prev.speed,
              lockSpeed: prev.lockSpeed,
            }
          : prev,
      );
      // Den Netzwerk-Aufruf entprellen: ein Slider-Zug feuert viele Events, aber es genügt der
      // letzte Stand. Änderungen werden bis zum Absenden gesammelt (cycleMs + speed).
      pendingPaceRef.current = { ...pendingPaceRef.current, ...change };
      if (paceTimerRef.current !== null) clearTimeout(paceTimerRef.current);
      paceTimerRef.current = setTimeout(() => {
        paceTimerRef.current = null;
        const payload = pendingPaceRef.current;
        pendingPaceRef.current = {};
        void updatePace(activeProjectId, runId, payload).catch((err) =>
          reportError(err, t('monitor.paceFailed')),
        );
      }, 150) as unknown as ReturnType<typeof setTimeout>;
    },
    [activeProjectId, runId, reportError, t],
  );

  const reconnect = useCallback(() => {
    if (activeProjectId && runId) openMonitor(activeProjectId, runId);
  }, [activeProjectId, runId, openMonitor]);

  // Beim Betreten der Ansicht / Projektwechsel prüfen, ob bereits ein Lauf aktiv ist (z. B. ein
  // per API/Deploy gestarteter Live-Lauf). Ist einer aktiv, hängt sich die HMI lesend an dessen
  // WS-Monitor (Badge „live", Werte fließen) — ohne einen zweiten Lauf zu starten. Beim Verlassen
  // / Projektwechsel wird die Verbindung sauber getrennt und der Zustand zurückgesetzt.
  useEffect(() => {
    if (!activeProjectId) {
      disconnect();
      return;
    }
    let cancelled = false;
    void listRuns(activeProjectId)
      .then((list) => {
        // Nur anhängen, solange die Ansicht/das Projekt noch dasselbe ist und wir nicht bereits
        // einen (selbst gestarteten) Lauf fahren — sonst würde der eigene Lauf überschrieben.
        if (cancelled || connectionRef.current !== null) return;
        if (list.active) {
          setSeries(new Map());
          setLatest(new Map());
          setSafety(null);
          setPaceState({
            cycleMs: list.active.cycleMs,
            speed: list.active.speed ?? 1,
            lockSpeed: list.active.lockSpeed ?? false,
          });
          setRunId(list.active.runId);
          setOwned(false);
          openMonitor(activeProjectId, list.active.runId);
        }
      })
      .catch(() => {
        /* keine aktive-Lauf-Abfrage möglich — dann bleibt es beim manuellen „Lauf starten" */
      });
    return () => {
      cancelled = true;
      disconnect();
    };
  }, [activeProjectId, disconnect, openMonitor]);

  return {
    runId,
    owned,
    conn,
    busy,
    series,
    latest,
    safety,
    pace,
    start,
    startLive,
    stop,
    reconnect,
    setPace,
  };
}
